import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

// Services
import useCampService from '../../../../../../../services/campService';

// Components
import InputField from '../../../../../../atoms/InputField/InputField';
import ToggleCheck from '../../../../../../atoms/ToggleCheck/ToggleCheck';
import TagsInput from '../../../../../../molecules/TagsInput/TagsInput';
import SkeletonLoader from '../../../../../../molecules/SkeletonLoader/SkeletonLoader';

function General({ formik }) {
  const { t } = useTranslation();

  const { getCampTypes, getCampSports } = useCampService();

  const { data: sports, isLoading: isSportsLoading } = useQuery('getCampSports', getCampSports);
  const { data: types, isLoading: isTypesLoading } = useQuery('getTypes', getCampTypes);

  const getInput = useCallback((name) => (
    <div className="m-b-15">
      <InputField
        id={`camp_marketing_${name}`}
        name={name}
        value={formik.values[name]}
        label={t(`camps.marketing.form.${name}`)}
        formik={{
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
        }}
        error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        isRequired
        full
      />
    </div>
  ), [formik]);

  const getInputTags = (name, options, isLoading, transKeyForOptions) => (
    isLoading ? (
      <div className="d-flex f-wrap">
        <SkeletonLoader width={100} height={40} className="m-r-10" radius={40} />
        <SkeletonLoader width={100} height={40} className="m-r-10" radius={40} />
        <SkeletonLoader width={100} height={40} className="m-r-10" radius={40} />
      </div>
    ) : (
      options?.length ? (
        <TagsInput
          name={name}
          options={options.map((option) => ({ label: t(`${transKeyForOptions}.${option}`), value: option }))}
          selected={formik.values[name]}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={formik.setFieldTouched}
          error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        />
      ) : null
    )
  );

  return (
    <div>
      <h3 className="fs-20 m-b-25">{t('camps.marketing.sub.informations')}</h3>

      <div className="input_max">
        <div className="m-b-30">
          <ToggleCheck
            labels={[t('camps.marketing.form.isNovelty')]}
            name="isNovelty"
            onValueChange={(checked) => formik.setFieldValue('isNovelty', checked)}
            isChecked={formik.values.isNovelty}
          />
        </div>

        {getInput('name')}
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-25">
        {t('camps.marketing.sub.sport')}
        <span className="c-primary-500 m-l-5 fs-18">*</span>
      </h3>

      <div className="input_max">
        {getInputTags('sport', sports?.data?.campSports, isSportsLoading, 'camps.sports')}
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-25">
        {t('camps.marketing.sub.type')}
        <span className="c-primary-500 m-l-5 fs-18">*</span>
      </h3>

      <div className="input_max">
        {getInputTags('type', types?.data?.campTypes, isTypesLoading, 'camps.types')}
      </div>
    </div>
  );
}

General.propTypes = {
  formik: PropTypes.shape({
    values: PropTypes.shape({
      isNovelty: PropTypes.bool,
      name: PropTypes.string,
      sport: PropTypes.shape({}),
      type: PropTypes.shape({}),
    }),
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    errors: PropTypes.shape({}),
    touched: PropTypes.shape({}),
  }).isRequired,
};

export default General;
